import {
  ATA_PRESENCA_LABEL,
  ATA_STATUS_LABEL,
  currentWeek,
  DECISION_STATUS_LABEL,
  DECISION_TIPO_LABEL,
  decisionStatusLabel,
  dueLabel,
  dueState,
  isWeekKey,
  OCCURRENCE_STATE_LABEL,
  PC_OPEN_STATUS_LABEL,
  PERFORMANCE_CLASS_LABEL,
  PMC_TASK_STATUS_LABEL,
  SGI_GOAL_LABEL,
  SGI_LAUNCH_LABEL,
  shiftWeek,
  TASK_PRIORITY_LABEL,
  TASK_STATUS_LABEL,
  todayInSaoPaulo,
  weekLabel,
  type AtaPresenca,
  type AtaStatus,
  type DecisionStatus,
  type DecisionTipo,
  type LoadBucket,
  type OccurrenceState,
  type PcTicketStatus,
  type PerformanceClass,
  type PmcTaskStatus,
  type SgiGoalCheck,
  type SgiLaunchStatus,
  type TaskPriority,
  type TaskStatus,
} from '@supply/contracts'
import { CalendarClock, CalendarDays, ChevronLeft, ChevronRight, Gavel, ListTodo } from 'lucide-react'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import type { Area } from '@/data/types'
import { useSettings } from '@/lib/session'
import { cn } from '@/lib/utils'

/**
 * Peças do domínio sobre o design system — status, prazo, área e semana
 * sempre por componente, nunca texto colorido solto (regra do DS).
 *
 * Cores (só tokens): pendente = warning · em processo = info ·
 * realizada = success · cancelada = muted riscado · atraso = destructive.
 */

type Tone = 'neutral' | 'warning' | 'info' | 'success' | 'danger'

const TONE: Record<Tone, { dot: string; bg: string; text: string }> = {
  neutral: { dot: 'bg-muted-foreground/60', bg: 'bg-muted', text: 'text-muted-foreground' },
  warning: { dot: 'bg-warning', bg: 'bg-warning/10', text: 'text-warning' },
  info: { dot: 'bg-info', bg: 'bg-info/10', text: 'text-info' },
  success: { dot: 'bg-success', bg: 'bg-success/10', text: 'text-success' },
  danger: { dot: 'bg-destructive', bg: 'bg-destructive/10', text: 'text-destructive' },
}

export const STATUS_TONE: Record<TaskStatus, Tone> = {
  pendente: 'warning',
  em_processo: 'info',
  realizada: 'success',
  cancelada: 'neutral',
}

/** Classe de fundo sólido por status (barras, pontos, gráficos). */
export const STATUS_BG: Record<TaskStatus, string> = {
  pendente: 'bg-warning',
  em_processo: 'bg-info',
  realizada: 'bg-success',
  cancelada: 'bg-muted-foreground/40',
}

/** Cor de gráfico por status — `hsl(var(--token))`, nunca hex. */
export const STATUS_CHART: Record<TaskStatus, string> = {
  pendente: 'hsl(var(--warning))',
  em_processo: 'hsl(var(--info))',
  realizada: 'hsl(var(--success))',
  cancelada: 'hsl(var(--muted-foreground) / 0.45)',
}

/** `plain`: sem fundo, só bolinha colorida + texto `muted` (linhas de metadado). */
function Pill({
  tone,
  children,
  className,
  strike,
  plain,
}: {
  tone: Tone
  children: React.ReactNode
  className?: string
  strike?: boolean
  plain?: boolean
}) {
  const t = TONE[tone]
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm text-xs font-medium',
        plain ? 'text-muted-foreground' : ['px-2 py-0.5', t.bg, t.text],
        strike && 'line-through',
        className,
      )}
    >
      <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />
      {children}
    </span>
  )
}

export function TaskStatusPill({ status, className, plain }: { status: TaskStatus; className?: string; plain?: boolean }) {
  return (
    <Pill tone={STATUS_TONE[status]} className={className} strike={status === 'cancelada'} plain={plain}>
      {TASK_STATUS_LABEL[status]}
    </Pill>
  )
}

/** Decisão: aberta pede ação (como pendente); virou tarefa/ação já está em andamento. */
export const DECISION_TONE: Record<DecisionStatus, Tone> = {
  aberta: 'warning',
  convertida: 'info',
  concluida: 'success',
  descartada: 'neutral',
}

/** Bolinha de status da decisão (itens de menu/select). */
export function DecisionStatusDot({ status, tipo, className }: { status: DecisionStatus; tipo?: DecisionTipo; className?: string }) {
  return <span aria-hidden className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE[decisionTone(status, tipo)].dot, className)} />
}

/** Tom do status conforme o tipo: registro vigente é informação, não pede ação. */
const decisionTone = (status: DecisionStatus, tipo: DecisionTipo = 'executar'): Tone =>
  tipo === 'registro' && status === 'aberta' ? 'info' : DECISION_TONE[status]

export function DecisionStatusPill({ status, tipo = 'executar', className }: { status: DecisionStatus; tipo?: DecisionTipo; className?: string }) {
  return (
    <Pill tone={decisionTone(status, tipo)} className={className} strike={status === 'descartada'}>
      {decisionStatusLabel(tipo, status, DECISION_STATUS_LABEL)}
    </Pill>
  )
}

/**
 * Tipo da decisão: A executar (contorno neutro, ícone de checklist) ou
 * Registro (fundo primário, martelo) — distinguíveis sem depender só da cor.
 */
export function DecisionTipoBadge({ tipo, className }: { tipo: DecisionTipo; className?: string }) {
  const Icon = tipo === 'registro' ? Gavel : ListTodo
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[11px] font-medium',
        tipo === 'registro' ? 'border-primary/30 bg-primary/10 text-primary' : 'border-border text-muted-foreground',
        className,
      )}
    >
      <Icon aria-hidden className="h-3 w-3" />
      {DECISION_TIPO_LABEL[tipo]}
    </span>
  )
}

/** Status da ATA: rascunho ainda muda; fechada está congelada; enviada saiu. */
export const ATA_STATUS_TONE: Record<AtaStatus, Tone> = {
  rascunho: 'warning',
  fechada: 'info',
  enviada: 'success',
}

/** Status da ATA com bolinha; sem ATA no banco, "Não iniciada". */
export function AtaStatusPill({ status, className, plain }: { status: AtaStatus | null; className?: string; plain?: boolean }) {
  return (
    <Pill tone={status ? ATA_STATUS_TONE[status] : 'neutral'} className={className} plain={plain}>
      {status ? ATA_STATUS_LABEL[status] : 'Não iniciada'}
    </Pill>
  )
}

/** Presença do convocado na ATA: a confirmar pede ação; faltou é alerta. */
export const PRESENCA_TONE: Record<AtaPresenca, Tone> = {
  pendente: 'warning',
  presente: 'success',
  ausente: 'danger',
}

export function PresencaPill({ presenca, className }: { presenca: AtaPresenca; className?: string }) {
  return (
    <Pill tone={PRESENCA_TONE[presenca]} className={className}>
      {ATA_PRESENCA_LABEL[presenca]}
    </Pill>
  )
}

/** Ocorrência de rotina: feita, não feita, pendente (hoje/futuro) ou passou sem marcação. */
export const OCCURRENCE_TONE: Record<OccurrenceState, Tone> = {
  feita: 'success',
  nao_feita: 'danger',
  pendente: 'neutral',
  perdida: 'warning',
}

export function OccurrencePill({ state, className }: { state: OccurrenceState; className?: string }) {
  return (
    <Pill tone={OCCURRENCE_TONE[state]} className={className}>
      {OCCURRENCE_STATE_LABEL[state]}
    </Pill>
  )
}

/**
 * Faixa de prazo da Carga do time (status reservado: sempre com rótulo).
 * Em dia = neutro (nada a cobrar); a vencer = atenção; atrasada = alerta.
 */
export const LOAD_BUCKET_BG: Record<LoadBucket, string> = {
  em_dia: 'bg-muted-foreground/45',
  a_vencer: 'bg-warning',
  atrasada: 'bg-destructive',
}

/** Prazo da decisão: data dd/mm/aaaa e, se aberta e apertada, o contador colorido. */
export function DecisionDue({ prazo, status, today = todayInSaoPaulo() }: { prazo: string | null; status: DecisionStatus; today?: string }) {
  const soon = useSettings()['prazo.dias_em_breve']
  if (!prazo) return <span className="text-xs text-muted-foreground">Sem prazo</span>
  const state = dueState(prazo, today, status === 'aberta' ? 'pendente' : 'realizada', soon)
  const urgent = state.kind === 'atrasada' || state.kind === 'hoje'
  const [y, m, d] = prazo.split('-')
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className={cn('font-mono text-xs tabular-nums', state.kind === 'atrasada' ? 'font-medium text-destructive' : 'text-foreground')}>
        {d}/{m}/{y}
      </span>
      {urgent && (
        <Pill tone={state.kind === 'atrasada' ? 'danger' : 'warning'} className="px-1.5 py-0 font-mono text-[11px] tabular-nums">
          {state.kind === 'hoje' ? 'hoje' : `${-(state.days ?? 0)}d atraso`}
        </Pill>
      )}
    </span>
  )
}

/** Fundo e texto da pílula, para gatilhos que imitam a `Pill` (select de status). */
export const decisionToneClass = (status: DecisionStatus, tipo?: DecisionTipo) =>
  cn(TONE[decisionTone(status, tipo)].bg, TONE[decisionTone(status, tipo)].text)

export function DueBadge({
  prazo,
  status,
  today = todayInSaoPaulo(),
  className,
}: {
  prazo: string | null
  status: TaskStatus
  today?: string
  className?: string
}) {
  const state = dueState(prazo, today, status, useSettings()['prazo.dias_em_breve'])
  const label = dueLabel(state)
  if (!label) return null
  const tone: Tone = state.kind === 'atrasada' ? 'danger' : state.kind === 'hoje' || state.kind === 'proxima' ? 'warning' : 'neutral'
  return (
    <Pill tone={tone} className={cn('font-mono tabular-nums', className)}>
      {label}
    </Pill>
  )
}

const PRIORITY_CLASS: Record<TaskPriority, string> = {
  baixa: 'border-muted-foreground/30 text-muted-foreground',
  media: 'border-info/40 text-info',
  alta: 'border-warning/50 text-warning',
  urgente: 'border-destructive/50 text-destructive',
}

export function PriorityTag({ priority, className }: { priority: TaskPriority; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border bg-transparent px-2 py-0.5 text-xs font-medium',
        PRIORITY_CLASS[priority],
        className,
      )}
    >
      {TASK_PRIORITY_LABEL[priority]}
    </span>
  )
}

/** Faixa de prioridade no card do quadro — sinal periférico (o texto vai em `sr-only`). */
export const PRIORITY_BAR: Record<TaskPriority, string> = {
  baixa: 'bg-muted-foreground/25',
  media: 'bg-info/60',
  alta: 'bg-warning',
  urgente: 'bg-destructive',
}

const DUE_TEXT: Record<string, string> = {
  atrasada: 'font-medium text-destructive',
  hoje: 'font-medium text-warning',
  proxima: 'text-warning',
  tranquila: 'text-muted-foreground',
  fechada: 'text-muted-foreground',
  'sem-prazo': 'text-muted-foreground',
}

/**
 * Prazo compacto do card (`dd/mm` com a cor do estado). O contador por
 * extenso fica no `title` e para o leitor de tela.
 */
export function DueDate({
  prazo,
  status,
  today = todayInSaoPaulo(),
  className,
}: {
  prazo: string | null
  status: TaskStatus
  today?: string
  className?: string
}) {
  const soon = useSettings()['prazo.dias_em_breve']
  if (!prazo) return null
  const state = dueState(prazo, today, status, soon)
  const label = dueLabel(state)
  const [, m, d] = prazo.split('-')
  return (
    <span className={cn('inline-flex items-center gap-1 font-mono text-[11px] tabular-nums', DUE_TEXT[state.kind], className)} title={label ?? undefined}>
      <CalendarDays className="h-3 w-3" aria-hidden />
      {d}/{m}
      {label && <span className="sr-only"> · {label}</span>}
    </span>
  )
}

export const PERFORMANCE_TONE: Record<PerformanceClass, Tone> = {
  bom: 'success',
  regular: 'warning',
  ruim: 'danger',
  'sem-dados': 'neutral',
}

export const PERFORMANCE_TEXT: Record<PerformanceClass, string> = {
  bom: 'text-success',
  regular: 'text-warning',
  ruim: 'text-destructive',
  'sem-dados': 'text-muted-foreground',
}

export const PERFORMANCE_BG: Record<PerformanceClass, string> = {
  bom: 'bg-success',
  regular: 'bg-warning',
  ruim: 'bg-destructive',
  'sem-dados': 'bg-muted-foreground/40',
}

export function PerformancePill({ value }: { value: PerformanceClass }) {
  return <Pill tone={PERFORMANCE_TONE[value]}>{PERFORMANCE_CLASS_LABEL[value]}</Pill>
}

/** Token de cor da área → classe (literal, para o Tailwind enxergar). */
const AREA_DOT: Record<string, string> = {
  'chart-1': 'bg-chart-1',
  'chart-2': 'bg-chart-2',
  'chart-3': 'bg-chart-3',
  'chart-4': 'bg-chart-4',
  'chart-5': 'bg-chart-5',
}
const AREA_BORDER: Record<string, string> = {
  'chart-1': 'border-t-chart-1',
  'chart-2': 'border-t-chart-2',
  'chart-3': 'border-t-chart-3',
  'chart-4': 'border-t-chart-4',
  'chart-5': 'border-t-chart-5',
}

export const areaDotClass = (area: Pick<Area, 'color_token'> | undefined) => AREA_DOT[area?.color_token ?? ''] ?? 'bg-muted-foreground/50'
export const areaBorderClass = (area: Pick<Area, 'color_token'> | undefined) =>
  AREA_BORDER[area?.color_token ?? ''] ?? 'border-t-border'
export const areaChartColor = (area: Pick<Area, 'color_token'> | undefined) =>
  area ? `hsl(var(--${area.color_token}))` : 'hsl(var(--muted-foreground))'

export function AreaTag({ area, className }: { area: Area | undefined; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-muted-foreground', className)}>
      <span aria-hidden className={cn('h-2 w-2 rounded-sm', areaDotClass(area))} />
      {area?.code ?? 'Sem área'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Semana e área na URL (compartilháveis: `?semana=2026-W43&area=<id>`)
// ---------------------------------------------------------------------------

export function useWeekParam(): [string, (week: string) => void] {
  const [params, setParams] = useSearchParams()
  const raw = params.get('semana')
  const week = raw && isWeekKey(raw) ? raw : currentWeek()
  const setWeek = React.useCallback(
    (next: string) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p)
          if (next === currentWeek()) n.delete('semana')
          else n.set('semana', next)
          return n
        },
        { replace: true },
      ),
    [setParams],
  )
  return [week, setWeek]
}

export function useParam(name: string): [string | null, (value: string | null) => void] {
  const [params, setParams] = useSearchParams()
  const value = params.get(name)
  const set = React.useCallback(
    (next: string | null) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p)
          if (next) n.set(name, next)
          else n.delete(name)
          return n
        },
        { replace: true },
      ),
    [name, setParams],
  )
  return [value, set]
}

export function WeekNav({ week, onChange, className }: { week: string; onChange: (w: string) => void; className?: string }) {
  const isCurrent = week === currentWeek()
  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <Button variant="outline" size="icon-sm" aria-label="Semana anterior" onClick={() => onChange(shiftWeek(week, -1))}>
        <ChevronLeft />
      </Button>
      <div className="min-w-[13.5rem] rounded-md border border-border px-3 py-1.5 text-center text-sm font-medium tabular-nums">
        {weekLabel(week)}
      </div>
      <Button variant="outline" size="icon-sm" aria-label="Próxima semana" onClick={() => onChange(shiftWeek(week, 1))}>
        <ChevronRight />
      </Button>
      {!isCurrent && (
        <Button variant="ghost" size="sm" onClick={() => onChange(currentWeek())}>
          <CalendarClock /> Semana atual
        </Button>
      )}
    </div>
  )
}

/** Filtro de área em pílulas (o toque na área do protótipo). */
export function AreaFilter({
  areas,
  value,
  onChange,
  counts,
}: {
  areas: readonly Area[]
  value: string | null
  onChange: (id: string | null) => void
  counts?: ReadonlyMap<string, number>
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar por área">
      <FilterChip active={!value} onClick={() => onChange(null)}>
        Todas
      </FilterChip>
      {areas.map((a) => (
        <FilterChip key={a.id} active={value === a.id} onClick={() => onChange(value === a.id ? null : a.id)}>
          <span aria-hidden className={cn('h-2 w-2 rounded-sm', areaDotClass(a))} />
          {a.code}
          {counts && <span className="font-mono text-[11px] tabular-nums opacity-70">{counts.get(a.id) ?? 0}</span>}
        </FilterChip>
      ))}
    </div>
  )
}

export function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background text-foreground hover:bg-accent',
      )}
    >
      {children}
    </button>
  )
}

/** Barra empilhada por status (o `stack` do protótipo). */
export function StatusStack({
  counts,
  className,
}: {
  counts: Readonly<Record<TaskStatus, number>>
  className?: string
}) {
  const total = Object.values(counts).reduce((s, n) => s + n, 0)
  return (
    <div
      className={cn('flex h-2 w-full overflow-hidden rounded-full bg-muted', className)}
      role="img"
      aria-label={(Object.keys(counts) as TaskStatus[]).map((k) => `${TASK_STATUS_LABEL[k]}: ${counts[k]}`).join(', ')}
    >
      {total > 0 &&
        (Object.keys(counts) as TaskStatus[]).map((k) =>
          counts[k] ? <i key={k} className={cn('block h-full', STATUS_BG[k])} style={{ width: `${(counts[k] / total) * 100}%` }} /> : null,
        )}
    </div>
  )
}

/** Barra de desempenho com o marcador da meta. */
export function TargetBar({ pct, target, cls }: { pct: number | null; target: number; cls: PerformanceClass }) {
  return (
    <div className="relative h-2.5 w-full rounded-full bg-muted" role="img" aria-label={`${pct ?? 0}% de ${target}% da meta`}>
      <div className={cn('absolute inset-y-0 left-0 rounded-full', PERFORMANCE_BG[cls])} style={{ width: `${Math.min(100, pct ?? 0)}%` }} />
      <div className="absolute -inset-y-1 w-0.5 bg-foreground/70" style={{ left: `${target}%` }} aria-hidden />
    </div>
  )
}

export function fmtQty(v: number): string {
  return Number.isInteger(v) ? String(v) : v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

// ---------------------------------------------------------------------------
// Outros sistemas (só leitura): PMC e SGI
// ---------------------------------------------------------------------------

const PMC_TONE: Record<PmcTaskStatus, Tone> = {
  todo: 'warning',
  in_progress: 'info',
  awaiting_approval: 'neutral',
  done: 'success',
  cancelled: 'neutral',
  late: 'danger',
}

/** Status da atividade do PMC, com os rótulos do PMC. Atrasada pelo prazo vira "Atrasada". */
export function PmcStatusPill({ status, late, className }: { status: PmcTaskStatus; late?: boolean; className?: string }) {
  const tone: Tone = late || status === 'late' ? 'danger' : PMC_TONE[status]
  return (
    <Pill tone={tone} className={className}>
      {late && status !== 'late' ? 'Atrasada' : PMC_TASK_STATUS_LABEL[status]}
    </Pill>
  )
}

const PC_TONE: Record<PcTicketStatus, Tone> = {
  Aberto: 'warning',
  'Em Andamento': 'info',
  Pausado: 'neutral',
  Concluído: 'success',
  Cancelado: 'neutral',
}

/** Status do chamado do Portal de Chamados. Aberto com o SLA de resolução vencido vira "Atrasado". */
export function PcStatusPill({ status, late, className }: { status: PcTicketStatus; late?: boolean; className?: string }) {
  const open = status === 'Aberto' || status === 'Em Andamento' || status === 'Pausado'
  return (
    <Pill tone={late && open ? 'danger' : PC_TONE[status]} className={className}>
      {late && open ? 'Atrasado' : open ? PC_OPEN_STATUS_LABEL[status] : status}
    </Pill>
  )
}

export const SGI_LAUNCH_TONE: Record<SgiLaunchStatus, Tone> = {
  lancado: 'success',
  pendente: 'warning',
  atrasado: 'danger',
  sem_lancamento: 'neutral',
  nao_fechado: 'neutral',
  nao_devido: 'neutral',
}

export function SgiLaunchPill({ status, className, children }: { status: SgiLaunchStatus; className?: string; children?: React.ReactNode }) {
  return (
    <Pill tone={SGI_LAUNCH_TONE[status]} className={className}>
      {children ?? SGI_LAUNCH_LABEL[status]}
    </Pill>
  )
}

/** Célula da matriz do SGI: quadrado com o tom e um sinal (não depende só da cor). */
export function SgiLaunchMark({ status, className }: { status: SgiLaunchStatus; className?: string }) {
  const t = TONE[SGI_LAUNCH_TONE[status]]
  const sign = { lancado: '✓', pendente: '!', atrasado: '×', sem_lancamento: '○', nao_fechado: '', nao_devido: '–' }[status]
  return (
    <span
      aria-label={SGI_LAUNCH_LABEL[status]}
      className={cn(
        'inline-flex h-6 w-6 items-center justify-center rounded-sm text-xs font-semibold',
        // Mês que ainda não fechou: só o contorno tracejado, para o ano inteiro ficar à vista.
        status === 'nao_fechado' ? 'border border-dashed border-border' : [t.bg, t.text],
        className,
      )}
    >
      <span aria-hidden>{sign}</span>
    </span>
  )
}

export function SgiGoalPill({ check, className }: { check: SgiGoalCheck; className?: string }) {
  const tone: Tone = check === 'na_meta' ? 'success' : check === 'fora_da_meta' ? 'danger' : 'neutral'
  return (
    <Pill tone={tone} plain={check === 'sem_meta'} className={className}>
      {SGI_GOAL_LABEL[check]}
    </Pill>
  )
}
